import React from 'react';
import clsx from 'clsx';
import { FilterableSelect } from '../ui/FilterableSelect';
import {
  PICKUP_MODES,
  PICKUP_MODE_OPTIONS,
  PICKUP_TIME_OPTIONS,
  applyPickupMode,
  describePickupChoice,
  pickupModeFromValues,
} from '../../features/sellers/sellerFormUtils';

/** One stretch of the day: two hours picked from a list, never typed. */
const Window = ({ title, from, to, onFromChange, onToChange, error, testId, disabled }) => (
  <div>
    <span className="block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5">
      {title}
    </span>
    <div className="grid grid-cols-2 gap-3">
      <FilterableSelect
        variant="form"
        label="Desde"
        testId={`${testId}-from`}
        value={from || ''}
        onChange={onFromChange}
        options={PICKUP_TIME_OPTIONS}
        disabled={disabled}
        placeholder="Hora"
        searchPlaceholder="Buscar hora..."
        hasError={Boolean(error)}
      />
      <FilterableSelect
        variant="form"
        label="Hasta"
        testId={`${testId}-to`}
        value={to || ''}
        onChange={onToChange}
        options={PICKUP_TIME_OPTIONS}
        disabled={disabled}
        placeholder="Hora"
        searchPlaceholder="Buscar hora..."
        hasError={Boolean(error)}
      />
    </div>
    {error && <p className="text-danger text-xs mt-1">{error}</p>}
  </div>
);

/**
 * When a courier may collect at a warehouse.
 *
 * It asks the question the seller can answer — whether the day has a break in
 * the middle — and only then the hours, from a list in half-hour steps. Typing
 * `HH:MM` by hand made half a window possible (one end stored, the other left
 * to the courier's default) and turned the commonest case, "nine to six", into
 * four keyboard entries.
 *
 * Fully controlled, and it patches with its own key names: whoever mounts it
 * maps them to the ones their form uses.
 *
 * @param {object} props
 * @param {boolean} props.allDay
 * @param {string} props.amFrom - `HH:MM`
 * @param {string} props.amTo
 * @param {string} props.pmFrom
 * @param {string} props.pmTo
 * @param {(patch: object) => void} props.onChange
 * @param {{am?: string, pm?: string}} [props.errors]
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.idPrefix='pickup-schedule'] - Names the radio group and
 *   every testid underneath.
 */
export const PickupScheduleFields = ({
  allDay,
  amFrom,
  amTo,
  pmFrom,
  pmTo,
  onChange,
  errors = {},
  disabled = false,
  idPrefix = 'pickup-schedule',
}) => {
  const values = { allDay, amFrom, amTo, pmFrom, pmTo };
  const mode = pickupModeFromValues(values);

  return (
    <div className="space-y-4">
      <span className="block text-sm font-semibold text-ninja-dark dark:text-white">
        ¿Cuándo puede pasar el courier?
      </span>

      <div className="grid gap-2">
        {PICKUP_MODE_OPTIONS.map((option) => (
          <label
            key={option.value}
            data-testid={`${idPrefix}-mode`}
            data-mode={option.value}
            className={clsx(
              'flex items-start gap-3 rounded-xl border p-3 transition-colors',
              mode === option.value
                ? 'border-ninja-green bg-ninja-greenLight/40 dark:bg-ninja-green/10'
                : 'border-border hover:border-text-muted',
              disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
            )}
          >
            <input
              type="radio"
              name={`${idPrefix}-mode`}
              value={option.value}
              checked={mode === option.value}
              disabled={disabled}
              onChange={() => onChange(applyPickupMode(option.value, values))}
              className="mt-0.5 w-4 h-4 accent-ninja-green"
            />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ninja-dark dark:text-white">
                {option.label}
              </span>
              <span className="block text-xs text-text-secondary mt-0.5">{option.description}</span>
            </span>
          </label>
        ))}
      </div>

      {mode !== PICKUP_MODES.ALL_DAY && (
        <div className="space-y-4">
          <Window
            title={mode === PICKUP_MODES.SPLIT ? 'Mañana' : 'Horario'}
            testId={`${idPrefix}-am`}
            from={amFrom}
            to={amTo}
            onFromChange={(value) => onChange({ amFrom: value })}
            onToChange={(value) => onChange({ amTo: value })}
            error={errors.am}
            disabled={disabled}
          />

          {mode === PICKUP_MODES.SPLIT && (
            <Window
              title="Tarde"
              testId={`${idPrefix}-pm`}
              from={pmFrom}
              to={pmTo}
              onFromChange={(value) => onChange({ pmFrom: value })}
              onToChange={(value) => onChange({ pmTo: value })}
              error={errors.pm}
              disabled={disabled}
            />
          )}
        </div>
      )}

      <p data-testid={`${idPrefix}-summary`} className="text-sm text-text-secondary">
        {describePickupChoice(values)}
      </p>
    </div>
  );
};
